// src/pages/admin/Customers/CustomerDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Calendar, Package, CreditCard } from 'lucide-react';
import AdminHeader from '../../../components/admin/common/AdminHeader';
import AdminBreadcrumb from '../../../components/admin/common/AdminBreadcrumb';
import StatusBadge from '../../../components/admin/common/StatusBadge';
import AdminTable from '../../../components/admin/common/AdminTable';
import { customerService, type Customer } from '../../../services/customerService';
import { orderService, type Order } from '../../../services/orderService';
import { formatPrice, formatDate } from '../../../utils/helpers';

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    fetchCustomerData();
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const [customerData, customerOrders] = await Promise.all([
        customerService.getCustomer(id!),
        orderService.getOrdersByCustomer(id!)
      ]);

      if (customerData) {
        setCustomer(customerData);
        setOrders(customerOrders);
      } else {
        setError('Customer not found');
      }
    } catch (err) {
      console.error('Error fetching customer data:', err);
      setError('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error || 'Customer not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminBreadcrumb 
        items={[
          { label: 'Customers', href: '/admin/customers' },
          { label: customer.name }
        ]} 
      />

      <AdminHeader 
        title="Customer Details"
        actions={
          <Link
            to={`/admin/customers/${id}/edit`}
            className="px-4 py-2 bg-brand-blue-500 text-white rounded-lg 
              hover:bg-brand-blue-600 transition-colors"
          >
            Edit Customer
          </Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-medium">{customer.name}</h3>
                <p className="text-gray-500">{customer.email}</p>
              </div>
              <StatusBadge status={customer.status} />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500">Email</label>
                  <div className="flex items-center mt-1">
                    <Mail className="w-5 h-5 mr-2 text-gray-400" />
                    <span>{customer.email}</span>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-gray-500">Phone</label>
                  <div className="flex items-center mt-1">
                    <Phone className="w-5 h-5 mr-2 text-gray-400" />
                    <span>{customer.phone}</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Member Since</label>
                  <div className="flex items-center mt-1">
                    <Calendar className="w-5 h-5 mr-2 text-gray-400" />
                    <span>{formatDate(customer.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-500">Address</label>
                <div className="flex items-start mt-1">
                  <MapPin className="w-5 h-5 mr-2 text-gray-400 mt-1" />
                  <div>
                    <p>{customer.address.street}</p>
                    <p>{customer.address.city}, {customer.address.state}</p>
                    <p>{customer.address.country}, {customer.address.postalCode}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order History */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b">
              <h3 className="text-lg font-medium">Order History</h3>
            </div>
            <AdminTable
              columns={[
                {
                  key: 'orderNumber',
                  title: 'Order ID',
                  render: (order: Order) => (
                    <Link
                      to={`/admin/orders/${order.id}`}
                      className="font-medium text-brand-blue-500 hover:text-brand-blue-600"
                    >
                      #{order.orderNumber}
                    </Link>
                  )
                },
                {
                  key: 'date',
                  title: 'Date',
                  render: (order: Order) => formatDate(order.createdAt.toDate())
                },
                {
                  key: 'status',
                  title: 'Status',
                  render: (order: Order) => (
                    <StatusBadge status={order.status} size="sm" />
                  )
                },
                {
                  key: 'items',
                  title: 'Items',
                  render: (order: Order) => order.items.length
                },
                {
                  key: 'total',
                  title: 'Total',
                  render: (order: Order) => (
                    <span className="font-medium">
                      {formatPrice(order.total)}
                    </span>
                  )
                }
              ]}
              data={orders}
              isLoading={loading}
            />
          </div>
        </div>

        {/* Customer Stats */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium mb-6">Customer Stats</h3>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-600">
                  <Package className="w-5 h-5 mr-2" />
                  <span>Total Orders</span>
                </div>
                <span className="font-medium">{customer.totalOrders}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-600">
                  <CreditCard className="w-5 h-5 mr-2" />
                  <span>Total Spent</span>
                </div>
                <span className="font-medium">
                  {formatPrice(customer.totalSpent)}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-5 h-5 mr-2" />
                  <span>Last Order</span>
                </div>
                <span>{formatDate(customer.lastOrder)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;